import { isToday } from "date-fns";
import { CalendarDays, CalendarX2, ExternalLink } from "lucide-react";
import Button from "../../../components/Button/Button";
import Loader from "../../../components/Loader/Loader";
import useStudentTimetable from "./hooks/useStudentTimetable";
import { useStudentSession } from "../context/StudentSessionContext";
import TimetablePeriodCard from "./components/TimetablePeriodCard";
import "./Timetable.css";

const toInputValue = (date) => {
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60000);
  return local.toISOString().slice(0, 10);
};

const formatDisplayDate = (date) =>
  date.toLocaleDateString(undefined, { day: "2-digit", month: "long", year: "numeric" });

const isImageUrl = (value) => typeof value === "string" && /\.(png|jpe?g|gif|webp|bmp)(\?.*)?$/i.test(value);

const StudentTimetable = () => {
  const { student } = useStudentSession();
  const { selectedDate, setSelectedDate, periods, fileUrl, isLoading } = useStudentTimetable(
    student?.id,
    student?.student_program?.program_id,
    student?.student_program?.session_id
  );

  const handleDateChange = (event) => {
    const value = event.target.value;
    if (!value) return;
    const [year, month, day] = value.split("-").map(Number);
    setSelectedDate(new Date(year, month - 1, day));
  };

  return (
    <div className="student-timetable">
      <div className="sp-card timetable-date-card">
        <h4 className="sp-section-title">
          <span className="sp-icon-tile" data-tone="primary">
            <CalendarDays size={18} />
          </span>
          Select Date
        </h4>
        <div className="timetable-date-input-wrap">
          <CalendarDays size={16} aria-hidden="true" />
          <input
            type="date"
            className="timetable-date-input"
            value={toInputValue(selectedDate)}
            onChange={handleDateChange}
            aria-label="Select timetable date"
          />
        </div>
        <Button variant="link" size="sm" className="timetable-today-btn" onClick={() => setSelectedDate(new Date())}>
          Jump to today
        </Button>
      </div>

      <div className="sp-card timetable-content-card">
        <h4 className="sp-section-title timetable-content-title">
          <span className="sp-icon-tile" data-tone="primary">
            <CalendarDays size={18} />
          </span>
          {formatDisplayDate(selectedDate)}
          {isToday(selectedDate) && <span className="timetable-today-badge">Today</span>}
        </h4>

        {isLoading ? (
          <Loader />
        ) : periods ? (
          <div className="timetable-period-list">
            {periods.map((row, index) => (
              <TimetablePeriodCard key={row.id ?? index} row={row} index={index} />
            ))}
          </div>
        ) : fileUrl ? (
          isImageUrl(fileUrl) ? (
            <img src={fileUrl} alt="Timetable" className="timetable-image" />
          ) : (
            // TODO: timetable_docs came back as a relative path in testing
            // (e.g. not prefixed with http(s)://) with no confirmed base URL
            // to resolve it against (student/details' profile_image has the
            // same gap) — opened as-is until the real file base URL/view
            // endpoint is confirmed.
            <Button
              variant="primary"
              className="timetable-view-link"
              icon={<ExternalLink size={16} />}
              onClick={() => window.open(fileUrl, "_blank", "noopener,noreferrer")}
            >
              View Timetable
            </Button>
          )
        ) : (
          <div className="sp-empty-state">
            <CalendarX2 size={40} strokeWidth={1.5} />
            <p>No timetable available for this date.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentTimetable;
